/**
 * VirtualSpace brand language, shared by the website and the apps (AD #694).
 *
 * Source: the Webflow prototype export (founder, 2026-09-24), plus the fixes
 * the live site got right (44px targets, Nasalization headline numbers).
 * Surfaces get lighter as they rise, depth is a black shadow (never a red
 * glow), and there are two reds (founder, 2026-09-25):
 *
 * - #C20000, the logo red: every fill (buttons, checked controls, pins,
 *   email buttons; white text on it is 6.4:1) and all red on light
 *   backgrounds (6.4:1 on white, 5.9:1 on the light canvas).
 * - #FF4D4D, the same hue lightened: any red on dark backgrounds that is not a
 *   fill (text of any size, borders, dots, selected states, map strokes).
 *   The logo red is only 2.6:1 on the charcoal canvas. #FF4D4D is 5.1:1 there
 *   and 4.7:1 on raised tiles.
 *
 * On dark, `redAccent` is the same value as `redText`. The key stays so the
 * `--brand-red-accent` consumers keep a name for "red that isn't a fill".
 *
 * `brand.css` declares every value here as a `--brand-*` custom property and
 * `brand-tokens.test.ts` keeps the two equal. `website-brand.ts` reads the
 * reds and text tiers from here for Webflow embed CSS.
 */

export const BRAND_FONT_DISPLAY = "Nasalization,Tahoma,sans-serif";
export const BRAND_FONT_TEXT = "Hind,Tahoma,sans-serif";

/** Dark (default) palette. Tiles are white at 2/3/5% over the canvas, made opaque. */
export const BRAND_DARK = {
  canvas: "#1C1D22",
  tile: "#212226",
  tileRaised: "#232429",
  tileHover: "#27282D",
  floating: "#2A2C31",
  line: "rgba(255,255,255,0.12)",
  lineSoft: "rgba(255,255,255,0.07)",
  lineStrong: "rgba(255,255,255,0.22)",
  text: "#FFFFFF",
  textSecondary: "rgba(255,255,255,0.7)",
  textTertiary: "rgba(255,255,255,0.5)",
  textDisabled: "rgba(255,255,255,0.3)",
  /**
   * Muted text as an opaque colour (white 70% over the canvas). The shadcn
   * `--muted-foreground` takes it so `text-muted-foreground/70` style
   * modifiers dim a solid grey instead of compounding two alphas (~35%).
   */
  mutedText: "#BBBBBD",
  /** Keyboard focus ring (WCAG 1.4.11 wants 3:1 against what it sits on). */
  focusRing: "rgba(255,255,255,0.6)",
  redFill: "#C20000",
  redPressed: "#A00000",
  redAccent: "#FF4D4D",
  redText: "#FF4D4D",
  redTextHover: "#FF6666",
  /**
   * Status text for tinted chips and words (Badge tones, billing tones): the
   * values the dark theme already used, now declared by the scope itself so
   * a `<BrandScope theme="dark" />` pin on a light `html` gets them too.
   */
  successText: "#7DE3B0",
  warningText: "#FBD27A",
  infoText: "#9BD9EF",
} as const;

/**
 * Light palette: the same roles on #F5F5F7 with white tiles. The logo red
 * reads as text on light (5.9:1 on the canvas, 6.4:1 on white), so it covers
 * every red role there.
 */
export const BRAND_LIGHT = {
  canvas: "#F5F5F7",
  tile: "#FFFFFF",
  tileRaised: "#FFFFFF",
  tileHover: "#FAFAFB",
  inset: "#EEEEF0",
  floating: "#FFFFFF",
  line: "rgba(28,29,34,0.12)",
  lineSoft: "rgba(28,29,34,0.07)",
  lineStrong: "rgba(28,29,34,0.24)",
  text: "#1C1D22",
  textSecondary: "rgba(28,29,34,0.75)",
  textTertiary: "rgba(28,29,34,0.64)",
  textDisabled: "rgba(28,29,34,0.38)",
  mutedText: "#6A6A6E",
  focusRing: "rgba(28,29,34,0.55)",
  redFill: "#C20000",
  redPressed: "#A00000",
  redAccent: "#C20000",
  redText: "#C20000",
  redTextHover: "#A00000",
  /** Status text on light (the `.vs-on-light` values): 6.6–8.2:1 on white and the canvas. */
  successText: "#0B653B",
  warningText: "#704600",
  infoText: "#075B7A",
} as const;

/**
 * Workflow stage hues (AD #788). The founder, 2026-09-27, on the neutral
 * Production Lab: "you've definitely taken away a bit too much color and now
 * it feels very monotone". The stages take back the hues the dispatch
 * convention gave them before P6.1 (AD #756) made the tiles neutral: Incoming
 * violet, Field zinc, Edit amber, QA blue, Deliver green, Shipped emerald.
 *
 * A stage hue names and marks its stage (words, edges, dots, a light wash);
 * it is never a fill behind text, and never red, which stays the action and
 * the focus. Dark takes the 300 steps (8–11:1 on the tiles); light takes the
 * 700–800 steps, which keep 4.5:1 on the muted inset and on the current
 * tile's wash.
 */
export const BRAND_STAGE_DARK = {
  incoming: "#C4B5FD",
  field: "#D4D4D8",
  edit: "#FCD34D",
  qa: "#93C5FD",
  deliver: "#86EFAC",
  shipped: "#6EE7B7",
} as const;

export const BRAND_STAGE_LIGHT = {
  incoming: "#6D28D9",
  field: "#3F3F46",
  edit: "#92400E",
  qa: "#1D4ED8",
  deliver: "#166534",
  shipped: "#065F46",
} as const;

export type BrandStage = keyof typeof BRAND_STAGE_DARK;

export const BRAND_STAGES = Object.keys(BRAND_STAGE_DARK) as BrandStage[];

/** Shape and depth: 8px everywhere, .3rem buttons, pills; black shadows only. */
export const BRAND_SHAPE = {
  radius: "8px",
  radiusButton: "0.3rem",
  radiusPill: "999px",
  shadowRest: "0 10px 40px rgba(0,0,0,0.2)",
  shadowLift: "0 40px 30px -30px rgba(0,0,0,0.7)",
  blur: "30px",
} as const;

/**
 * Colours the brand retires. The live-site cleanup (2026-09-25) removed them
 * from the Webflow Designer and the app-served embeds; the Webflow-side embed
 * copies that still carry them are listed in `website-brand.test.ts` and are
 * overridden by the footer polish embed (`website-brand-overrides.ts`).
 * #D00000 and #E5141B were the interim fill and accent reds, replaced by the
 * logo red and #FF4D4D the same day.
 */
export const BRAND_RETIRED_COLORS = [
  "#FF0000",
  "#D00000",
  "#E5141B",
  "#C41118",
  "#121316",
  "#0D0D18",
  "#12121A",
  "#0A0A12",
] as const;

/**
 * Flattens an `rgba(r,g,b,a)` token onto an opaque `#RRGGBB` ground and
 * returns `#RRGGBB`. For places that can't composite alpha: email clients
 * (Outlook drops rgba) and static image APIs. Hex input is returned as is.
 */
export function flattenOver(color: string, ground: string): string {
  if (color.startsWith("#")) return color.toUpperCase();
  const [r, g, b, a] = color.match(/[\d.]+/g)!.map(Number);
  const base = [1, 3, 5].map((i) => parseInt(ground.slice(i, i + 2), 16));
  const mix = [r, g, b].map((v, i) => Math.round(v * a + base[i] * (1 - a)));
  return `#${mix.map((v) => v.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/** Space-separated HSL channels for the shadcn bridge, from a #RRGGBB hex. */
export function hexToHslChannels(hex: string): string {
  const value = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map(
    (i) => parseInt(value.slice(i, i + 2), 16) / 255,
  );
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  let hue = 0;
  let saturation = 0;
  if (delta !== 0) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
    if (max === r) hue = 60 * (((g - b) / delta) % 6);
    else if (max === g) hue = 60 * ((b - r) / delta + 2);
    else hue = 60 * ((r - g) / delta + 4);
  }
  if (hue < 0) hue += 360;
  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

/** Every `--brand-*` custom property `brand.css` must declare on `:root`. */
export function brandCssVariables(): Record<string, string> {
  const d = BRAND_DARK;
  const l = BRAND_LIGHT;
  return {
    "--brand-canvas": d.canvas,
    "--brand-tile": d.tile,
    "--brand-tile-raised": d.tileRaised,
    "--brand-tile-hover": d.tileHover,
    "--brand-floating": d.floating,
    "--brand-line": d.line,
    "--brand-line-soft": d.lineSoft,
    "--brand-line-strong": d.lineStrong,
    "--brand-text": d.text,
    "--brand-text-secondary": d.textSecondary,
    "--brand-text-tertiary": d.textTertiary,
    "--brand-text-disabled": d.textDisabled,
    "--brand-muted-text": d.mutedText,
    "--brand-focus-ring": d.focusRing,
    "--brand-red-fill": d.redFill,
    "--brand-red-pressed": d.redPressed,
    "--brand-red-accent": d.redAccent,
    "--brand-red-text": d.redText,
    "--brand-red-text-hover": d.redTextHover,
    "--brand-success-text": d.successText,
    "--brand-warning-text": d.warningText,
    "--brand-info-text": d.infoText,
    "--brand-light-canvas": l.canvas,
    "--brand-light-tile": l.tile,
    "--brand-light-tile-hover": l.tileHover,
    "--brand-light-inset": l.inset,
    "--brand-light-line": l.line,
    "--brand-light-line-soft": l.lineSoft,
    "--brand-light-line-strong": l.lineStrong,
    "--brand-light-text": l.text,
    "--brand-light-text-secondary": l.textSecondary,
    "--brand-light-text-tertiary": l.textTertiary,
    "--brand-light-text-disabled": l.textDisabled,
    "--brand-light-muted-text": l.mutedText,
    "--brand-light-focus-ring": l.focusRing,
    "--brand-light-success-text": l.successText,
    "--brand-light-warning-text": l.warningText,
    "--brand-light-info-text": l.infoText,
    ...Object.fromEntries(
      BRAND_STAGES.map((stage) => [`--brand-stage-${stage}`, BRAND_STAGE_DARK[stage]]),
    ),
    ...Object.fromEntries(
      BRAND_STAGES.map((stage) => [`--brand-light-stage-${stage}`, BRAND_STAGE_LIGHT[stage]]),
    ),
    "--brand-radius": BRAND_SHAPE.radius,
    "--brand-radius-button": BRAND_SHAPE.radiusButton,
    "--brand-radius-pill": BRAND_SHAPE.radiusPill,
    "--brand-shadow-rest": BRAND_SHAPE.shadowRest,
    "--brand-shadow-lift": BRAND_SHAPE.shadowLift,
    "--brand-blur": BRAND_SHAPE.blur,
    "--brand-hsl-canvas": hexToHslChannels(d.canvas),
    "--brand-hsl-tile": hexToHslChannels(d.tile),
    "--brand-hsl-tile-raised": hexToHslChannels(d.tileRaised),
    "--brand-hsl-tile-hover": hexToHslChannels(d.tileHover),
    "--brand-hsl-floating": hexToHslChannels(d.floating),
    "--brand-hsl-red-fill": hexToHslChannels(d.redFill),
    "--brand-hsl-red-accent": hexToHslChannels(d.redAccent),
    "--brand-hsl-light-canvas": hexToHslChannels(l.canvas),
    "--brand-hsl-light-inset": hexToHslChannels(l.inset),
    "--brand-hsl-light-text": hexToHslChannels(l.text),
    "--brand-hsl-muted-text": hexToHslChannels(d.mutedText),
    "--brand-hsl-light-muted-text": hexToHslChannels(l.mutedText),
  };
}
